import type { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import { useKeyModificationDialogStore } from 'components/WorkspaceEditor/components/KeyModificationWarningDialog'
import { DesignationField } from 'components/WorkspaceEditor/features/drawingDesignation'
import { shouldHideElement } from 'components/WorkspaceEditor/features/drawingDesignation/utils/elementVisibility'
import type { DataNode } from 'components/WorkspaceEditor/types'
import {
    ADDITIONAL_ELEMENT_SUFFIX,
    isManufacturerOrBODKeyedPara,
    shouldRenderAsProductType,
} from 'components/WorkspaceEditor/utils'
import React, { memo, useCallback, useMemo, useRef } from 'react'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { preserveTrailingSpaceWithoutNbsp } from '../../ContentEditor/contentUtils'
import { EmptyPlainTextContainer } from '../EmptyPlainTextContainer'
import { EditorContent } from './EditorContent'
import { KeyedText } from './KeyedText'
import { usePlainTextController } from './hooks/usePlainTextController'
import { usePlainTextStyles } from './usePlainTextStyles'

enum DrawingDesignationType {
    INSERTDRAWINGDESIGNATION = 'INSERTDRAWINGDESIGNATION',
    NONE = 'NONE',
}

// Utility functions
const generateSuggestionDataAttribute = (suggestionId?: string) => {
    return suggestionId ? `content-suggestion-id-${suggestionId}` : undefined
}

/**
 * Shows the PT key modification warning dialog.
 * Used when user clicks on a non-editable PT key portion.
 */
const showPTKeyWarning = (keyText: string) => {
    const { showDialog, closeDialog } = useKeyModificationDialogStore.getState()
    showDialog({
        isPTKey: true,
        originalKey: keyText,
        modifiedKey: keyText,
        onProceed: closeDialog,
        onCancel: closeDialog,
    })
}

/** Props for the PlainText component. */
export interface PlainTextProps {
    /** The contentItem object that holds the text property that needs to render */
    contentItem: Partial<TextContentElement>
    /** The parent node, used for Product Type detection and key-portion analysis */
    node?: DataNode
    /** Whether the component is inactive */
    inactive?: boolean
    /** Whether the component is editable */
    editable?: boolean
    /** The classes to apply the styling */
    classes?: string
}

/**
 * An editable component that renders plain text. It uses Typography (caption variant) from Material-UI as a placeholder for CKEditor (ContentEditor).
 * Can be editable based on:
 * - by default, the component is not editable
 * - when the component has changes (initial suggestions)
 * - when focused or clicked
 * @param {PlainTextProps} props - Props for the component.
 */
export const PlainText = memo<PlainTextProps>(
    ({ contentItem, node, inactive = false, editable = false, classes }) => {
        /** controller props and handlers */
        const {
            text,
            keyedText,
            keyedTextValue,
            formattedContentId,
            initialData,
            initialSuggestions,
            clickCoordinates,
            ckeToggle,
            shouldRenderContentEditor,
            isKeyedText,
            hasDesignationFormat,
            hasValueTextAfterFieldRef,
            isContentTeamFromStudioPage,
            handlers,
        } = usePlainTextController(contentItem, node)

        const plainTextStyles = usePlainTextStyles()
        const containerRef = useRef<HTMLSpanElement>(null)
        const { isCurrentlyOnOMWorkspace } = useOMWorkspace()
        const { previewFeatures } = usePreviewFeatures()

        /** MFR/BOD header's first element (e.g. "Manufacturers:", "Basis-of-Design Product:") is read-only. */
        const isMfrBodHeader = useMemo(
            () =>
                !!node &&
                contentItem.id === node.contentItemNodes?.[0]?.id &&
                isManufacturerOrBODKeyedPara(node),
            [node, contentItem.id]
        )

        // Prevent CKEditor from trimming trailing spaces
        const cleanedInitialData = useMemo(
            () => preserveTrailingSpaceWithoutNbsp(initialData),
            [initialData]
        )
        // Allow editing inactive content in OM workspace, except for deactivated nodes.
        // Note: isDeactivated=true always implies inactive=true (TreeDataMapper folds isDeactivated
        // into isHidden, which drives the inactive prop), so the case isDeactivated=true&&inactive=false
        // cannot occur in practice — canEditInactive will always be false for deactivated nodes.
        // Remove the hasOMPTAutomation condition when the feature is production ready.
        const isDeactivatedInOM =
            previewFeatures.hasOMPTAutomation && node?.isDeactivated
        const canEditInactive =
            (isCurrentlyOnOMWorkspace && !isDeactivatedInOM) || !inactive
        const isEditMode =
            shouldRenderContentEditor &&
            canEditInactive &&
            editable &&
            !isMfrBodHeader
        const showEmptyPlainTextContainer =
            isKeyedText && !keyedTextValue && !inactive && !isEditMode
        const displayText = text ?? ''
        const hasEmptyText = !text?.trim()

        // Render as a drawing-designation field only for Product Types, not user-added/pasted paragraphs.
        const isProductTypeNode = shouldRenderAsProductType(node)

        // Check if this is the first content item in a PT (Product Type name/key portion)
        // PT names can be split across multiple content items where the first item
        // is the key (e.g., "Cementitious Patching Mortar") without a colon
        const isPTFirstItem = Boolean(
            isProductTypeNode &&
                contentItem.id === node?.contentItemNodes?.[0]?.id
        )

        // Check if this is the first content item in an Article
        const isArticle = node?.type === 'Article'
        const isArticleFirstItem = Boolean(
            isArticle &&
                !node?.isUserAdded &&
                contentItem.id === node?.contentItemNodes?.[0]?.id
        )

        // Only the FIRST element should render the DesignationField to avoid duplication
        // Applies to Product Types and Articles WITH product type icon (isProductType=true)
        // Articles don't require isKeyedText (no colon in article format)
        // Regular articles without product type icon should NOT show DesignationField
        const isArticleWithProductType = isArticle && isProductTypeNode
        const isEditDrawingDesignationMode =
            (node?.pTDesignationKey === undefined ||
                node?.pTDesignationKey === DrawingDesignationType.NONE ||
                node?.pTDesignationKey ===
                    DrawingDesignationType.INSERTDRAWINGDESIGNATION) && // Only allow PT's
            isProductTypeNode &&
            (isKeyedText || isArticleWithProductType || hasDesignationFormat) &&
            contentItem.id &&
            node?.key &&
            (isPTFirstItem || (isArticleWithProductType && isArticleFirstItem)) // Only first element renders designation

        // PT key = first content item only (Bug 2574397: prevents duplicate DesignationField)
        const isPTKey = Boolean(isProductTypeNode && isPTFirstItem)

        // Memoize container class to prevent recreation on each render
        const containerClass = useMemo(() => {
            const showPlainTextContainer =
                !editable || isDeactivatedInOM || isMfrBodHeader
            if (showPlainTextContainer)
                return plainTextStyles.plainTextContainerReadOnly
            if (hasEmptyText) return plainTextStyles.plainTextContainer
            return plainTextStyles.plainTextContainerWithContent
        }, [
            editable,
            hasEmptyText,
            isDeactivatedInOM,
            isMfrBodHeader,
            plainTextStyles,
        ])

        // Generate suggestion data attribute
        const suggestionDataAttribute = useMemo(
            () => generateSuggestionDataAttribute(initialSuggestions?.[0]?.id),
            [initialSuggestions]
        )

        // Focus handler that blocks activation for locked keys when not in edit mode
        const handleFocusCapture = useCallback(
            (event?: React.FocusEvent<HTMLElement>) => {
                // MFR/BOD header rows are always non-editable
                if (isMfrBodHeader) {
                    return
                }

                // For designation mode, don't activate on focus - let the click handler decide
                // This prevents the editor from activating before the click handler can show the PT key warning
                if (isEditDrawingDesignationMode) {
                    return
                }

                // For locked keys not in edit mode, don't activate editor on focus
                // Let the onClick handler determine if we should activate based on where they clicked
                if (isPTKey && !isEditMode) {
                    return
                }

                // For all other cases, activate the editor
                handlers.onFocusCapture(event)
            },
            [
                isPTKey,
                isEditMode,
                isEditDrawingDesignationMode,
                isMfrBodHeader,
                handlers,
            ]
        )

        // Click handler that checks if clicking on locked key portion
        const handleClick = useCallback(
            (event: React.MouseEvent<HTMLElement>) => {
                // MFR/BOD header rows are always non-editable
                if (isMfrBodHeader) {
                    event.preventDefault()
                    event.stopPropagation()
                    return
                }

                const target = event.target as HTMLElement
                const canShowWarning = !inactive && !isContentTeamFromStudioPage

                // Check click targets
                const clickedOnDesignationPrefix = target.closest(
                    '[data-testid="designation-prefix"]'
                )
                const clickedOnKeyedText = target.closest(
                    '[data-testid^="keyed-text"]'
                )

                // 1. Designation prefix click (PT key in designation mode)
                if (clickedOnDesignationPrefix && isPTKey && canShowWarning) {
                    event.preventDefault()
                    event.stopPropagation()
                    showPTKeyWarning(keyedText || '')
                    return
                }

                // 2. Drawing designation mode
                if (isEditDrawingDesignationMode) {
                    // Single-element PTs have no separate value PlainText —
                    // the suffix is rendered by DesignationField, so activate
                    // this PlainText's CKEditor when clicking on the suffix area
                    if (!hasValueTextAfterFieldRef) {
                        const clickedOnDesignationSuffix = target.closest(
                            '[data-testid="designation-suffix"]'
                        )
                        if (clickedOnDesignationSuffix) {
                            handlers.onFocusCapture()
                        }
                    }
                    handlers.onClick(event)
                    return
                }

                // 3. PT first items (non-keyed) - entire content is the key.
                // Content team/OM users are excluded from key locking entirely (isKeyedText
                // is false for them even when a real colon-delimited key exists), so let
                // their clicks fall through to normal editor activation instead of blocking.
                if (
                    isPTFirstItem &&
                    !isKeyedText &&
                    !isContentTeamFromStudioPage
                ) {
                    event.preventDefault()
                    event.stopPropagation()
                    if (!inactive) {
                        showPTKeyWarning(text || '')
                    }
                    return
                }

                // 4. Locked keyed text - block clicks on key portion
                if (isPTKey && clickedOnKeyedText) {
                    // Let event propagate to KeyedText for its own dialog handling
                    return
                }

                // 5. Locked keys clicking on value - activate editor
                if (isPTKey) {
                    handlers.onFocusCapture()
                }

                handlers.onClick(event)
            },
            [
                isPTKey,
                isPTFirstItem,
                isKeyedText,
                text,
                keyedText,
                inactive,
                isContentTeamFromStudioPage,
                handlers,
                isEditDrawingDesignationMode,
                hasValueTextAfterFieldRef,
                isMfrBodHeader,
            ]
        )

        // Check if element should be hidden (Drawing Designation feature)
        if (
            shouldHideElement({
                node,
                contentItem,
                isPTFirstItem,
            })
        ) {
            return null
        }

        return (
            <Typography
                variant="caption"
                key={formattedContentId}
                data-testid={formattedContentId}
                data-suggestion={suggestionDataAttribute}
                className={clsx(classes, containerClass)}
                tabIndex={0}
                ref={ckeToggle.active ? ckeToggle.placeholderRef : containerRef}
                onKeyDown={handlers.onKeyDown}
                onKeyUp={handlers.onKeyUp}
                onFocusCapture={handleFocusCapture}
                onClick={handleClick}
                onMouseMove={handlers.onMouseMove}
            >
                {isEditMode ? (
                    <EditorContent
                        initialData={cleanedInitialData}
                        initialSuggestions={initialSuggestions}
                        clickCoordinates={clickCoordinates}
                        handlers={handlers}
                        keyedText={keyedText}
                        keyedTextValue={keyedTextValue}
                        classes={classes}
                        designationProps={{
                            isProductType: isProductTypeNode,
                            isArticle: isArticleWithProductType,
                            contentItemId: contentItem.id,
                            nodeId: node?.key,
                            isPTKey,
                            serverDesignation:
                                node?.contentItemView?.drawingDesignation,
                            pTDesignationKey: node?.pTDesignationKey,
                        }}
                    />
                ) : isEditDrawingDesignationMode ? (
                    <DesignationField
                        keyedText={keyedText}
                        keyedTextValue={keyedTextValue}
                        contentItemId={contentItem?.id || ''}
                        editable={true}
                        inactive={false}
                        isArticle={isArticleWithProductType}
                        showSuffix={
                            !isArticleWithProductType &&
                            !hasValueTextAfterFieldRef
                        }
                        serverDesignation={
                            node?.contentItemView?.drawingDesignation
                        }
                    />
                ) : isPTKey && keyedTextValue !== undefined ? (
                    // Real colon-delimited key/value split — for locked keys (isKeyedText)
                    // as well as OM/content-team excluded content that still has a real
                    // key (isKeyedText is false for them, but keyedTextValue is still
                    // populated). Render key and value separately so a click on the key
                    // alone still shows the PT-key warning while the value stays clickable
                    // to activate the editor.
                    <>
                        <KeyedText
                            keyedText={keyedText}
                            classes={classes}
                            data-testid="keyed-text-key-display"
                            isPTKey={isPTKey}
                        />
                        {keyedTextValue}
                    </>
                ) : (
                    displayText
                )}
                {showEmptyPlainTextContainer && (
                    <EmptyPlainTextContainer
                        element={{
                            id: `${contentItem.id}${ADDITIONAL_ELEMENT_SUFFIX}`,
                            text: '',
                            type: contentItem.type,
                        }}
                        inactive={inactive}
                        editable={true}
                        node={node}
                    />
                )}
            </Typography>
        )
    }
)
